export const MICROUSD_PER_USD = 1_000_000;

// UI formatting helpers (duration, tokens, cost, date/time). Run lengths mirror the shared package;
// the remaining web-only helpers live here.

import { localDateTime } from './localDateTime';
import { formatBytes as sharedFormatBytes, formatCompactCount } from './displayFormat';

/** Parse an ISO 8601 timestamp to epoch ms, or null when it is missing or unparseable. */
export function parseTs(iso?: string | null): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

// ---------------------------------------------------------------------------------------------
// Duration
// ---------------------------------------------------------------------------------------------

/** WEB MIRROR of `formatDuration` in `packages/plugin-shared/duration.mjs`, the one run-length ladder the
 *  CLI, platform cards and plugins import. The web cannot import it: Turbopack resolves modules only inside
 *  the web root, so a symlinked package outside it fails `next dev` and `build:web`. Edit the source first,
 *  then copy its body here; `tests/contract/webMirrorContracts.test.ts` keeps both byte-identical. */
export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

/** Single-unit elapsed ladder (ms → "12s" / "3m" / "5h" / "2d"). Picks the largest unit that
 *  fits, so it reads at a glance — unlike the two-unit `formatDuration`. Negatives clamp to "0s".
 *  Single source of truth for the compact relative-time chips across the UI. */
export function compactElapsed(ms: number): string {
  const secs = Math.max(0, Math.floor(ms / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** A recording/call clock in minutes and seconds. Callers clamp their own elapsed input. */
export function formatClock(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------------------------
// Tokens & cost
// ---------------------------------------------------------------------------------------------

/** Compact token count: 950 → "950", 1234 → "1.2k", 12_345 → "12k", 1_200_000 → "1.2M". */
export function formatTokens(n: number): string {
  return formatCompactCount(n);
}

/** Exact usage figures in the viewer's UI locale, shared by the modal and model summary. The one-decimal
 *  cap is deliberate: these are token and chart-axis figures, not raw counts, and a plain localized count
 *  would print three decimals. Charts pass a minimum of one to retain their fixed-decimal percentage labels. */
export function formatUsageCount(value: number, locale: string, minimumFractionDigits: 0 | 1 = 0): string {
  return new Intl.NumberFormat(locale, { minimumFractionDigits, maximumFractionDigits: 1 }).format(value);
}

/** A USD amount in the viewer's locale — the ONE localized USD rendering, shared with plugin bundles. A
 *  `null` amount means nothing reported a price, and it renders an em dash rather than "$0.00": printing
 *  an unpriced bucket as free is a claim the data does not make. `decimals` defaults to 2, the value the
 *  published contract gives a bundle; every host caller passes 4, so the host renders every usage figure
 *  at 4dp. */
export function formatUsd(value: number | null, locale: string, decimals = 2): string {
  if (value == null) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(value);
}

/** Human-readable byte size on the 1024 ladder: "512 B", "1.5 KB", "20 MB", "1.0 GB". One decimal below
 *  10 units, none above, so a size column stays narrow without losing resolution where it matters.
 *  Single source of truth for on-disk sizes across the UI — plugin data footprints and log files.
 *
 *  Anything not a positive finite number renders "0 B". The unit index is clamped at both ends: an
 *  unclamped Math.log ratio goes NEGATIVE below one byte and is NaN for a NaN input, and indexing the
 *  unit array with either produced the literal "512 undefined" / "NaN undefined" this replaces. */
export function formatBytes(bytes: number, locale = 'en'): string {
  return sharedFormatBytes(bytes, locale);
}

// ---------------------------------------------------------------------------------------------
// Date & time
// ---------------------------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

export interface TaskTimeLabel {
  /** Compact relative label when the timestamp is within the last 24h ('45s','12m','3h'),
    * otherwise a locale-formatted date ('Jun 19, 14:02'). */
  label: string;
  /** Full absolute timestamp in the viewer's LOCAL time, for a title tooltip. DB timestamps
    * are UTC, so this converts them — otherwise the tooltip shows a confusing UTC clock. */
  title: string;
}

export { localDateTime } from './localDateTime';

/** Format a task timestamp as a single, unambiguous time label: a compact relative
 *  elapsed when it's within the last 24h, otherwise a locale date. The absolute local
 *  time is returned alongside for a title tooltip so it's always reachable (and never
 *  shows raw UTC). Falls back to the input string when it cannot be parsed. */
export function formatTaskTime(iso: string | null | undefined, nowMs: number, locale?: string): TaskTimeLabel {
  if (!iso) return { label: '', title: '' };
  const ms = parseTs(iso);
  if (ms == null) return { label: iso, title: iso };
  const title = localDateTime(iso, locale);
  const delta = nowMs - ms;
  if (delta < DAY_MS) return { label: compactElapsed(delta), title };
  const label = new Date(ms).toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  return { label, title };
}
